// Admissions CRM — shared frontend constants for the applications admin UI.
//
// These MIRROR the server-side definitions in backend/server.py (the
// "ADMISSIONS CRM — Commit A" section). Keep them in sync with:
//   - ProgramLiteral, legacy intended-semester values, GenderLiteral,
//     StatusInCanadaLiteral, CampusLiteral, AttendanceModeLiteral,
//     UniversityPathwayLiteral, ApplicationStatusLiteral
//   - ALLOWED_STATUS_TRANSITIONS
// The server is the source of truth and re-validates everything; this file
// just drives the UI so we never offer a transition the backend would reject.

import {
  formatDisplayDate,
  formatDateTimeValue,
} from './dates';

export const PROGRAM_OPTIONS = [
  'AI Microcredential',
  'Caregiver Senior/Infant',
  'Video & Content Creation',
  'Business Communication',
  'International Esports Management Diploma',
  'Hospitality Operations Diploma',
  'Global Management in Hospitality',
  'English (ESL)',
  'French Language',
  'TCF Canada',
  'TEF Canada',
];

export const SEMESTER_OPTIONS = [
  'Winter 2026', 'Spring 2026', 'Summer 2026', 'Fall 2026',
  'Winter 2027', 'Spring 2027', 'Summer 2027', 'Fall 2027',
];

export const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

export const STATUS_IN_CANADA_OPTIONS = [
  { value: 'citizen', label: 'Citizen' },
  { value: 'permanent_resident', label: 'Permanent resident' },
  { value: 'study_permit', label: 'Study permit' },
  { value: 'work_permit', label: 'Work permit' },
  { value: 'visitor_visa', label: 'Visitor visa' },
  { value: 'outside_canada', label: 'Outside Canada' },
  { value: 'other', label: 'Other' },
];

export const CAMPUS_OPTIONS = [
  { value: 'vancouver_downtown', label: 'Vancouver (Downtown)' },
  { value: 'online', label: 'Online' },
  { value: 'undecided', label: 'Undecided' },
];

export const ATTENDANCE_OPTIONS = [
  { value: 'in_person', label: 'In Person' },
  { value: 'online_live', label: 'Online (Live)' },
  { value: 'online_self_paced', label: 'Online (Self-paced)' },
  { value: 'blended', label: 'Blended' },
];

export const PATHWAY_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'undecided', label: 'Undecided' },
];

// Supporting-document types (mirror the backend allowlist for application docs).
export const DOC_TYPE_OPTIONS = [
  { value: 'passport', label: 'Passport' },
  { value: 'study_permit', label: 'Study permit' },
  { value: 'transcripts', label: 'Transcripts' },
  { value: 'english_results', label: 'English test results' },
  { value: 'french_results', label: 'French test results (TCF/TEF)' },
];

// Status badge metadata. `tone` maps to the badgeClass tones used across the
// admin app (neutral | warning | success | danger).
export const STATUS_META = {
  applied: { label: 'Applied', tone: 'neutral' },
  under_review: { label: 'Under review', tone: 'warning' },
  offered: { label: 'Offered', tone: 'success' },
  waitlisted: { label: 'Waitlisted', tone: 'warning' },
  rejected: { label: 'Rejected', tone: 'danger' },
  enrolled: { label: 'Enrolled', tone: 'success' },
};

export const ALL_STATUSES = Object.keys(STATUS_META);

// Mirror of backend ALLOWED_STATUS_TRANSITIONS. `enrolled` is intentionally
// never a transition target — it's set only by the Commit D convert-to-student
// flow, so it can never be picked from this UI.
export const ALLOWED_STATUS_TRANSITIONS = {
  applied: ['under_review', 'offered', 'waitlisted', 'rejected'],
  under_review: ['offered', 'waitlisted', 'rejected', 'applied'],
  waitlisted: ['under_review', 'offered', 'rejected'],
  offered: ['waitlisted', 'rejected', 'under_review'],
  rejected: ['under_review', 'applied'],
  enrolled: [],
};

export function statusMeta(status) {
  return STATUS_META[status] || { label: status || 'Unknown', tone: 'neutral' };
}

// Options to show in the status control: the current status plus every status
// the server would allow transitioning to from it.
export function statusOptionsFor(currentStatus) {
  const next = ALLOWED_STATUS_TRANSITIONS[currentStatus] || [];
  return [currentStatus, ...next].filter((v, i, arr) => v && arr.indexOf(v) === i);
}

export function labelFromOptions(options, value) {
  const normalizedValue = value === 'hybrid' ? 'blended' : value === 'online' ? 'online_live' : value;
  const found = options.find((o) => o.value === normalizedValue);
  return found ? found.label : value || '—';
}

export function formatDate(value) {
  if (!value) return '—';
  return formatDisplayDate(value);
}

export function formatDateTime(value) {
  if (!value) return '—';
  return formatDateTimeValue(value);
}

// Shared tone -> tailwind class map, matching the admin app's badge styling.
export function badgeClass(tone) {
  if (tone === 'danger') return 'border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.08)] text-[rgb(153,27,27)]';
  if (tone === 'warning') return 'border-[rgba(212, 175, 55,0.35)] bg-[rgba(212, 175, 55,0.10)] text-[#9A6D06]';
  if (tone === 'success') return 'border-[rgba(34,197,94,0.25)] bg-[rgba(34,197,94,0.10)] text-[rgb(21,128,61)]';
  return 'border-[rgba(6, 43, 26,0.12)] bg-[rgba(6, 43, 26,0.04)] text-[#062B1A]/70';
}
